import { useEffect, useState } from "react";
import api from "../lib/api";
import { getWithRetry } from "../lib/fetchWithRetry";
import LoadFailureNote from "../components/LoadFailureNote";
import { Card, CardContent } from "../components/ui/card";
import StatsFilterBar from "../components/StatsFilterBar";
import CareerStatSections from "../components/CareerStatSections";
import { toStatsQuery } from "../lib/statsQuery";
import { AreaChart, Area, LineChart, Line, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const AXIS = { fontSize: 11, fill: "#64748B" };
const TT = { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 };

export default function Statistics() {
  const [d, setD] = useState(null);
  // Issue #271: "we couldn't load it" is a third state alongside "loading"
  // and "here it is". `reloadKey` is what the Try again button bumps.
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Filter selection persists per page (issue #55) — mirrors the
  // nw_perf_* localStorage key pattern from PerformanceTracking.jsx.
  const [season, setSeason] = useState(() => localStorage.getItem("nw_stats_season") ?? "all");
  const [comp, setComp] = useState(() => localStorage.getItem("nw_stats_comp") ?? "all");
  // Issue #78: team filter, same persistence pattern.
  const [team, setTeam] = useState(() => localStorage.getItem("nw_stats_team") ?? "all");
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [compOptions, setCompOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);

  useEffect(() => { try { localStorage.setItem("nw_stats_season", season); } catch { /* quota / private mode */ } }, [season]);
  useEffect(() => { try { localStorage.setItem("nw_stats_comp", comp); } catch { /* quota / private mode */ } }, [comp]);
  useEffect(() => { try { localStorage.setItem("nw_stats_team", team); } catch { /* quota / private mode */ } }, [team]);

  // Filter options come from the player's real matches, never a hardcoded
  // list (issue #55) — all three endpoints mirror /matches/seasons.
  //
  // Issue #271, deliberately ignored (same call as Dashboard.jsx's copy of
  // these three): they populate optional dropdown contents behind an "all"
  // default. A failure costs the ability to narrow, never the correctness
  // of a displayed number.
  useEffect(() => {
    api.get("/matches/seasons").then((r) => setSeasonOptions(r.data?.seasons || [])).catch(() => {});
    api.get("/matches/competitions").then((r) => setCompOptions(r.data?.competitions || [])).catch(() => {});
    api.get("/matches/teams").then((r) => setTeamOptions(r.data?.teams || [])).catch(() => {});
  }, []);

  // Issue #271: this had no `.catch` at all — the rejection went unhandled
  // and `d` stayed null, so the page sat on "Loading…" forever. That never
  // showed a WRONG number (the guard below returns before any tile
  // renders), which is why it is a lesser bug than the Dashboard's, but a
  // spinner that resolves for no one is still a screen that never tells the
  // truth. Same treatment: retry the idempotent GET, then say so.
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    // Query built by the shared `toStatsQuery` (issue #460) — extracted, not
    // rewritten, so the coach's Career snapshot and this page cannot drift
    // apart on what "all" or a chosen filter puts on the wire.
    getWithRetry(`/stats/summary${toStatsQuery({ season, competition: comp, team })}`)
      .then((r) => { if (!cancelled) setD(r.data); })
      .catch((e) => {
        if (cancelled) return;
        // Drop the previous filter's data rather than leave it on screen
        // under the new filter's heading.
        setD(null);
        setFailed(true);
        console.error("Statistics: failed to load /stats/summary", e);
      });
    return () => { cancelled = true; };
  }, [season, comp, team, reloadKey]);

  if (failed) return (
    <div data-testid="statistics-page">
      <LoadFailureNote what="your statistics" testId="stats-error" onRetry={() => setReloadKey((k) => k + 1)} />
    </div>
  );
  if (!d) return <div className="text-slate-500">Loading…</div>;
  const trend = d.trend || [];
  const filterActive = season !== "all" || comp !== "all" || team !== "all";
  const headingSuffix = [season !== "all" ? season : null, comp !== "all" ? comp : null, team !== "all" ? team : null]
    .filter(Boolean)
    .join(" · ");
  const isEmpty = filterActive && d.matches === 0;

  return (
    <div className="space-y-6" data-testid="statistics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Lifelong record</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1" data-testid="statistics-heading">
            Statistics Dashboard{headingSuffix && <span className="text-slate-400"> · {headingSuffix}</span>}
          </h1>
          <p className="text-slate-500 mt-1">A complete, glanceable view of your career.</p>
        </div>
        {/* Issue #460: the row itself moved to StatsFilterBar so the coach's
            Career snapshot renders the same control. Options and persistence
            stay here — they are this page's own (localStorage, session
            player), per that component's header. */}
        <StatsFilterBar
          competition={comp}
          season={season}
          team={team}
          competitionOptions={compOptions}
          seasonOptions={seasonOptions}
          teamOptions={teamOptions}
          onCompetitionChange={setComp}
          onSeasonChange={setSeason}
          onTeamChange={setTeam}
        />
      </div>

      {isEmpty ? (
        <Card className="border-slate-200" data-testid="statistics-empty">
          <CardContent className="p-12 text-center">
            <p className="text-slate-500">
              No matches for {season === "all" ? "any season" : season} in {comp === "all" ? "any competition" : comp}
              {team !== "all" && <> for {team}</>}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Issue #460: the three dashboards ("Career highlights",
              "Fielding & wicket-keeping", "Bowling record") moved verbatim
              into CareerStatSections, shared with the coach's Career
              snapshot. Same tiles, same testids, same numbers. */}
          <CareerStatSections d={d} />

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Runs over time" testid="chart-runs">
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="gRuns" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0B2E59" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#0B2E59" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="match" tick={AXIS} />
                  <YAxis tick={AXIS} />
                  <Tooltip contentStyle={TT} />
                  <Area type="monotone" dataKey="runs" stroke="#0B2E59" strokeWidth={2.5} fill="url(#gRuns)" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Batting average trend" testid="chart-avg">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="match" tick={AXIS} />
                  <YAxis tick={AXIS} />
                  <Tooltip contentStyle={TT} />
                  <Line type="monotone" dataKey="avg" stroke="#D4A017" strokeWidth={3} dot={{ fill: "#D4A017", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Strike rate trend" testid="chart-sr">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="match" tick={AXIS} />
                  <YAxis tick={AXIS} />
                  <Tooltip contentStyle={TT} />
                  <Line type="monotone" dataKey="sr" stroke="#84C5FF" strokeWidth={3} dot={{ fill: "#0B2E59", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Wickets over time" testid="chart-wkts">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="match" tick={AXIS} />
                  <YAxis tick={AXIS} allowDecimals={false} />
                  <Tooltip contentStyle={TT} />
                  <Bar dataKey="wickets" fill="#D4A017" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Economy rate trend" testid="chart-econ" full>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="gEcon" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#84C5FF" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#84C5FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="match" tick={AXIS} />
                  <YAxis tick={AXIS} />
                  <Tooltip contentStyle={TT} />
                  <Area type="monotone" dataKey="econ" stroke="#0B2E59" strokeWidth={2.5} fill="url(#gEcon)" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}

function ChartCard({ title, children, testid, full }) {
  return (
    <Card className={`border-slate-200 ${full ? "lg:col-span-2" : ""}`} data-testid={testid}>
      <CardContent className="p-6">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-4">{title}</h3>
        {children}
      </CardContent>
    </Card>
  );
}
